import type { FC, ReactNode } from 'react';
import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Container, Stack, SvgIcon, Typography } from '@mui/material';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import { RouterLink } from 'src/components/router-link';
import { paths } from 'src/paths';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';

// Map display service names to their URL slugs for canonical schema URLs
const SERVICE_SLUGS: Record<string, string> = {
  'Branding': 'branding',
  'Websites': 'websites',
  'Marketing': 'marketing',
  'Content': 'content',
  'AI Automation': 'ai-automation'
};

interface Deliverable {
  num: string;
  title: string;
  body: string;
}

interface ProcessStep {
  num: string;
  title: string;
  body: string;
}

interface WhyItem {
  title: string;
  body: string;
}

interface FaqItem {
  q: string;
  a: string;
}

export interface ServicePageProps {
  service: string;
  eyebrow: string;
  headline: string;
  accent: string;
  sub: string;
  description: string;
  deliverables: Deliverable[];
  outcomes: string[];
  process?: ProcessStep[];
  whyUs?: WhyItem[];
  faqs?: FaqItem[];
  seoTitle: string;
  seoDescription: string;
}

const Reveal = ({ children, delay = 0 }: { children: ReactNode; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // threshold 0: fire as soon as ANY pixel enters the viewport.
        // A non-zero threshold can never be met by an element taller
        // than the viewport (intersectionRatio caps at viewportH/elH),
        // which made tall mobile sections stay invisible. Once shown,
        // disconnect so it never hides again.
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.98)',
        filter: visible ? 'blur(0)' : 'blur(6px)',
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, filter 0.7s ease ${delay}s`,
        willChange: 'opacity, transform, filter'
      }}
    >
      {children}
    </Box>
  );
};

const SectionLabel = ({ children }: { children: string }) => (
  <Stack direction="row" alignItems="center" spacing={1.25}>
    <Box sx={{ width: 24, height: 1, background: '#0a0a0b' }} />
    <Typography
      sx={{
        fontFamily: '"Roboto Mono", monospace',
        fontSize: 11,
        fontWeight: 500,
        color: '#0a0a0b',
        letterSpacing: 1.4,
        textTransform: 'uppercase'
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const sectionShellSx = {
  py: { xs: 12, md: 18 },
  borderTop: '1px solid #f4f4f5'
} as const;

const sectionTitleSx = {
  fontFamily: '"Inter", sans-serif',
  fontSize: { xs: '36px', md: '60px' },
  fontWeight: 700,
  lineHeight: 1.04,
  letterSpacing: '-0.035em',
  color: '#0a0a0b'
} as const;

export const ServicePage: FC<ServicePageProps> = ({
  service,
  eyebrow,
  headline,
  accent,
  sub,
  description,
  deliverables,
  outcomes,
  process,
  whyUs,
  faqs,
  seoTitle,
  seoDescription
}) => {
  usePageView();

  const serviceSlug = SERVICE_SLUGS[service] || service.toLowerCase().replace(/\s+/g, '-');
  const serviceUrl = `https://mysticmediafilm.com/services/${serviceSlug}`;
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${serviceUrl}#service`,
    name: service,
    description: seoDescription,
    url: serviceUrl,
    serviceType: service,
    provider: { '@id': 'https://mysticmediafilm.com/#organization' },
    areaServed: [
      { '@type': 'City', name: 'Houston' },
      { '@type': 'AdministrativeArea', name: 'Greater Houston Metropolitan Area' },
      { '@type': 'AdministrativeArea', name: 'Harris County' },
      { '@type': 'AdministrativeArea', name: 'Fort Bend County' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `${service} deliverables`,
      itemListElement: deliverables.map((d) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: d.title, description: d.body }
      }))
    }
  };

  return (
    <>
      <Seo title={seoTitle} description={seoDescription} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <main style={{ background: '#ffffff' }}>
        {/* Hero */}
        <Box
          sx={{
            background: '#ffffff',
            position: 'relative',
            pt: { xs: '140px', md: '180px' },
            pb: { xs: '60px', md: '90px' }
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={{ xs: 4, md: 5 }} sx={{ maxWidth: 960, mx: 'auto', textAlign: 'center', alignItems: 'center' }}>
              <Stack direction="row" justifyContent="center">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    pl: 0.75,
                    pr: 2,
                    py: 0.5,
                    borderRadius: 999,
                    background: '#ffffff',
                    border: '1px solid #e4e4e7',
                    boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      fontSize: 12,
                      fontWeight: 700
                    }}
                  >
                    ✶
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#0a0a0b',
                      letterSpacing: '-0.005em'
                    }}
                  >
                    Service · <Box component="span" sx={{ fontWeight: 700 }}>{service}</Box>
                  </Typography>
                </Stack>
              </Stack>

              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '44px', sm: '64px', md: '88px' },
                  fontWeight: 700,
                  lineHeight: 1.02,
                  letterSpacing: '-0.04em',
                  color: '#0a0a0b'
                }}
              >
                {headline}{' '}
                <Box component="span" sx={{ color: '#2563eb', fontStyle: 'italic' }}>
                  {accent}
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: 16, md: 19 },
                  fontWeight: 400,
                  lineHeight: 1.55,
                  letterSpacing: '-0.01em',
                  color: '#52525b',
                  maxWidth: 640
                }}
              >
                {sub}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" sx={{ pt: 1 }}>
                <Button
                  component={RouterLink}
                  href={paths.contact}
                  endIcon={(
                    <SvgIcon sx={{ fontSize: 16 }}>
                      <ArrowRightIcon />
                    </SvgIcon>
                  )}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    background: '#0a0a0b',
                    color: '#ffffff',
                    fontWeight: 500,
                    fontSize: 15,
                    letterSpacing: '-0.01em',
                    px: 2.75,
                    py: 1.5,
                    borderRadius: 999,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { background: '#27272a', boxShadow: 'none' }
                  }}
                  variant="contained"
                >
                  Start a project
                </Button>
                <Button
                  component={RouterLink}
                  href={paths.index}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#0a0a0b',
                    fontWeight: 500,
                    fontSize: 15,
                    letterSpacing: '-0.01em',
                    px: 2.75,
                    py: 1.5,
                    borderRadius: 999,
                    textTransform: 'none',
                    border: '1px solid #e4e4e7',
                    background: '#ffffff',
                    '&:hover': { borderColor: '#0a0a0b', background: '#fafafa' }
                  }}
                  variant="outlined"
                >
                  Back to home
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* Overview */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: '#0a0a0b',
                  color: '#ffffff',
                  px: { xs: 4, md: 7 },
                  py: { xs: 5, md: 7 }
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-30%',
                    right: '-10%',
                    width: 480,
                    height: 480,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37, 99, 235, 0.32) 0%, transparent 65%)',
                    filter: 'blur(60px)',
                    pointerEvents: 'none'
                  }}
                />

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1, mb: { xs: 4, md: 5 } }}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
                    <Typography
                      sx={{
                        fontFamily: '"Roboto Mono", monospace',
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'rgba(255,255,255,0.8)',
                        letterSpacing: 1.4,
                        textTransform: 'uppercase'
                      }}
                    >
                      {eyebrow}
                    </Typography>
                  </Stack>
                  <Box
                    component="img"
                    src="/assets/logo-main.png"
                    alt="Mystic Media Film"
                    sx={{ height: { xs: 24, md: 28 }, width: 'auto', opacity: 0.9 }}
                  />
                </Stack>

                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: 24, md: 38 },
                    fontWeight: 500,
                    lineHeight: 1.2,
                    letterSpacing: '-0.025em',
                    color: '#ffffff',
                    maxWidth: 880,
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {description.split('|')[0]}
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    {description.split('|')[1]}
                  </Box>
                </Typography>

                <Box
                  sx={{
                    mt: { xs: 5, md: 7 },
                    pt: { xs: 3, md: 4 },
                    borderTop: '1px solid rgba(255, 255, 255, 0.12)',
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${outcomes.length}, 1fr)` },
                    gap: { xs: 2, md: 0 },
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {outcomes.map((label, i) => (
                    <Stack
                      key={label}
                      direction="row"
                      alignItems="baseline"
                      spacing={1}
                      sx={{
                        pl: { md: i === 0 ? 0 : 2 },
                        borderLeft: { md: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.1)' }
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Roboto Mono", monospace',
                          fontSize: 10,
                          fontWeight: 400,
                          color: 'rgba(255,255,255,0.4)',
                          letterSpacing: 0.5
                        }}
                      >
                        0{i + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: 14,
                          fontWeight: 500,
                          color: '#ffffff',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        {label}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>
            </Reveal>
          </Container>
        </Box>

        {/* Deliverables */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack spacing={2.5}>
                  <SectionLabel>What you get</SectionLabel>
                  <Typography sx={sectionTitleSx}>
                    Everything you need{' '}
                    <Box component="span" sx={{ color: '#2563eb' }}>to ship.</Box>
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: { xs: 1.5, md: 2 }
                  }}
                >
                  {deliverables.map((d, i) => (
                    <Box
                      key={d.title}
                      sx={{
                        position: 'relative',
                        p: { xs: 3, md: 3.5 },
                        borderRadius: 3,
                        background: i === 0 ? '#0a0a0b' : '#fafafa',
                        border: '1px solid',
                        borderColor: i === 0 ? '#0a0a0b' : '#e4e4e7',
                        color: i === 0 ? '#ffffff' : '#0a0a0b',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        cursor: 'default',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          borderColor: i === 0 ? '#0a0a0b' : '#0a0a0b',
                          boxShadow: i === 0
                            ? '0 14px 30px rgba(37, 99, 235, 0.25)'
                            : '0 10px 24px rgba(15, 23, 42, 0.07)'
                        }
                      }}
                    >
                      {i === 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-20%',
                            width: 320,
                            height: 320,
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.5) 0%, transparent 65%)',
                            filter: 'blur(50px)',
                            pointerEvents: 'none'
                          }}
                        />
                      )}

                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4, position: 'relative', zIndex: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: '"Roboto Mono", monospace',
                            fontSize: 11,
                            fontWeight: 500,
                            letterSpacing: 0.6,
                            color: i === 0 ? 'rgba(255,255,255,0.55)' : '#a1a1aa'
                          }}
                        >
                          {d.num} / {String(deliverables.length).padStart(2, '0')}
                        </Typography>
                      </Stack>

                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: { xs: 24, md: 28 },
                          fontWeight: 600,
                          lineHeight: 1.05,
                          letterSpacing: '-0.03em',
                          color: i === 0 ? '#ffffff' : '#0a0a0b',
                          mb: 1.5,
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {d.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: 1.5,
                          color: i === 0 ? 'rgba(255,255,255,0.65)' : '#52525b',
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {d.body}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* Process */}
        {process && process.length > 0 && (
          <Box sx={sectionShellSx}>
            <Container maxWidth="lg">
              <Reveal>
                <Stack spacing={{ xs: 5, md: 7 }}>
                  <Stack spacing={2.5}>
                    <SectionLabel>How it works</SectionLabel>
                    <Typography sx={sectionTitleSx}>
                      A clear path{' '}
                      <Box component="span" sx={{ color: '#2563eb' }}>from kickoff to launch.</Box>
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${process.length}, 1fr)` },
                      gap: { xs: 3, md: 0 }
                    }}
                  >
                    {process.map((step, i) => (
                      <Stack
                        key={step.title}
                        spacing={2}
                        sx={{
                          px: { md: 3 },
                          py: { xs: 0, md: 1 },
                          borderLeft: { md: i === 0 ? 'none' : '1px solid #e4e4e7' }
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              background: '#0a0a0b',
                              color: '#ffffff',
                              fontFamily: '"Roboto Mono", monospace',
                              fontSize: 11,
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {step.num}
                          </Box>
                          <Box sx={{ flex: 1, height: 1, background: '#e4e4e7' }} />
                        </Stack>
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: 22, md: 24 },
                            fontWeight: 600,
                            lineHeight: 1.1,
                            letterSpacing: '-0.025em',
                            color: '#0a0a0b'
                          }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: 14,
                            fontWeight: 400,
                            lineHeight: 1.55,
                            color: '#52525b'
                          }}
                        >
                          {step.body}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </Stack>
              </Reveal>
            </Container>
          </Box>
        )}

        {/* Why Mystic */}
        {whyUs && whyUs.length > 0 && (
          <Box sx={sectionShellSx}>
            <Container maxWidth="lg">
              <Reveal>
                <Stack spacing={{ xs: 5, md: 7 }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                    spacing={{ xs: 3, md: 4 }}
                  >
                    <Stack spacing={2.5} sx={{ maxWidth: 720 }}>
                      <SectionLabel>Why Mystic</SectionLabel>
                      <Typography sx={sectionTitleSx}>
                        Built different.{' '}
                        <Box component="span" sx={{ color: '#2563eb' }}>On purpose.</Box>
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: 15, md: 16 },
                        fontWeight: 400,
                        lineHeight: 1.6,
                        color: '#52525b',
                        maxWidth: 360
                      }}
                    >
                      A small senior team, in-house craft, and a process that respects your time and your budget.
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: `repeat(${whyUs.length}, 1fr)` },
                      gap: { xs: 1.5, md: 2 }
                    }}
                  >
                    {whyUs.map((item, i) => (
                      <Box
                        key={item.title}
                        sx={{
                          position: 'relative',
                          p: { xs: 3, md: 4 },
                          borderRadius: 3,
                          background: '#ffffff',
                          border: '1px solid #e4e4e7',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#0a0a0b',
                            transform: 'translateY(-3px)',
                            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)'
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: '"Roboto Mono", monospace',
                            fontSize: 11,
                            fontWeight: 500,
                            color: '#2563eb',
                            letterSpacing: 0.6,
                            mb: 2
                          }}
                        >
                          0{i + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: 22, md: 24 },
                            fontWeight: 600,
                            lineHeight: 1.1,
                            letterSpacing: '-0.025em',
                            color: '#0a0a0b',
                            mb: 1.5
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: 14,
                            fontWeight: 400,
                            lineHeight: 1.55,
                            color: '#52525b'
                          }}
                        >
                          {item.body}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Reveal>
            </Container>
          </Box>
        )}

        {/* FAQ */}
        {faqs && faqs.length > 0 && (
          <Box sx={sectionShellSx}>
            <Container maxWidth="lg">
              <Reveal>
                <Stack spacing={{ xs: 5, md: 7 }}>
                  <Stack spacing={2.5}>
                    <SectionLabel>Common questions</SectionLabel>
                    <Typography sx={sectionTitleSx}>
                      Answers, not{' '}
                      <Box component="span" sx={{ color: '#2563eb' }}>fine print.</Box>
                    </Typography>
                  </Stack>

                  <Box>
                    {faqs.map((faq, i) => (
                      <Box
                        key={faq.q}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr' },
                          gap: { xs: 1.5, md: 6 },
                          py: { xs: 3.5, md: 4.5 },
                          borderTop: i === 0 ? '1px solid #e4e4e7' : 'none',
                          borderBottom: '1px solid #e4e4e7'
                        }}
                      >
                        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                          <Typography
                            sx={{
                              fontFamily: '"Roboto Mono", monospace',
                              fontSize: 11,
                              fontWeight: 500,
                              color: '#a1a1aa',
                              letterSpacing: 0.6,
                              pt: 0.75
                            }}
                          >
                            {String(i + 1).padStart(2, '0')}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"Inter", sans-serif',
                              fontSize: { xs: 18, md: 22 },
                              fontWeight: 600,
                              lineHeight: 1.25,
                              letterSpacing: '-0.02em',
                              color: '#0a0a0b'
                            }}
                          >
                            {faq.q}
                          </Typography>
                        </Stack>
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: 15, md: 16 },
                            fontWeight: 400,
                            lineHeight: 1.6,
                            color: '#52525b'
                          }}
                        >
                          {faq.a}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Reveal>
            </Container>
          </Box>
        )}

        {/* Final CTA */}
        <Box sx={{ ...sectionShellSx, py: { xs: 14, md: 20 } }}>
          <Container maxWidth="lg">
            <Reveal>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: '#0a0a0b',
                  color: '#ffffff',
                  px: { xs: 4, md: 8 },
                  py: { xs: 7, md: 10 }
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-30%',
                    right: '-10%',
                    width: 600,
                    height: 600,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37, 99, 235, 0.4) 0%, transparent 65%)',
                    filter: 'blur(70px)',
                    pointerEvents: 'none'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-30%',
                    left: '-10%',
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 65%)',
                    filter: 'blur(80px)',
                    pointerEvents: 'none'
                  }}
                />

                <Stack spacing={5} alignItems="flex-start" sx={{ position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
                    <Typography
                      sx={{
                        fontFamily: '"Roboto Mono", monospace',
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'rgba(255,255,255,0.8)',
                        letterSpacing: 1.4,
                        textTransform: 'uppercase'
                      }}
                    >
                      Get In Touch
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: { xs: '44px', md: '76px' },
                      fontWeight: 700,
                      lineHeight: 1.04,
                      letterSpacing: '-0.04em',
                      color: '#ffffff',
                      maxWidth: 880
                    }}
                  >
                    Ready to start{' '}
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                      your {service.toLowerCase()} project?
                    </Box>
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: { xs: 16, md: 18 },
                      fontWeight: 400,
                      lineHeight: 1.5,
                      color: 'rgba(255,255,255,0.6)',
                      maxWidth: 520
                    }}
                  >
                    Tell us about your project. We&apos;ll get back within one business day.
                  </Typography>

                  <Stack direction="row" spacing={1.5}>
                    <Button
                      component={RouterLink}
                      href={paths.contact}
                      endIcon={(
                        <SvgIcon sx={{ fontSize: 16 }}>
                          <ArrowRightIcon />
                        </SvgIcon>
                      )}
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        background: '#ffffff',
                        color: '#0a0a0b',
                        fontWeight: 500,
                        fontSize: 15,
                        letterSpacing: '-0.01em',
                        px: 2.75,
                        py: 1.5,
                        borderRadius: 999,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': { background: '#f4f4f5', boxShadow: 'none' }
                      }}
                      variant="contained"
                    >
                      Start your project
                    </Button>
                    <Button
                      component={RouterLink}
                      href={paths.contact}
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        color: '#ffffff',
                        fontWeight: 500,
                        fontSize: 15,
                        letterSpacing: '-0.01em',
                        px: 2.75,
                        py: 1.5,
                        borderRadius: 999,
                        textTransform: 'none',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'transparent',
                        '&:hover': { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.4)' }
                      }}
                      variant="outlined"
                    >
                      Email us
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Reveal>
          </Container>
        </Box>
      </main>
    </>
  );
};
